import { enrollCampaign,getInitialBotMessagesForPlaybook, getPersonalizedAnnouncementMessage } from 'api/campaigns'
import store, { getStoreState } from 'config/store'
import { triggerPlaybookFiredAPIEvent } from 'modules/api/events'
import { markMessageAsRead } from 'modules/conversations'
import { receivePreRenderedMessages } from 'modules/conversations/actions'
import { getEmbedId, getHorizontalOffset, getVerticalOffset } from 'modules/embed/selectors'
import { getBotSenderId, getEmbedLocale, getWidgetAlignment} from 'modules/embed/utils'
import { sendPlaybookImpression } from 'modules/impressions'
import { getCampaignRefreshToken } from 'modules/session/selectors'
import { toggleChat, toggleController } from 'modules/view/actions'
import { isChatOpen } from 'modules/view/selectors'
import { isNil, path, reject } from 'ramda'
import { WidgetFrames } from 'shared/constants/frames'
import { DRIFT_SESSION_CAMPAIGNS } from 'shared/constants/session'
import { CampaignDisplayType, PrerenderedAnnouncementResult, PrerenderedBotCampaign } from 'types/campaigns'
import { BotCampaignRecord  } from 'types/campaigns'
import { EmbedCampaign } from 'types/embed'
import { CTAType } from 'types/message'
import { warn } from 'utils'
import { broadcast } from 'utils/message'
import { localStore } from 'utils/storage'
import { linkifyMessageBody } from 'utils/url'

import { receiveEnrolledCampaign } from './actions'
import { markAnnouncementPlaybookOpened } from './announcements'
import { assignActiveCampaignFromEmbed } from './index'
import { getContextForPreRenderedCampaigns } from './selectors'
import { RenderBotCampaignResult } from './types'
import { getAttributeFromCampaignOrDefault, getCampaignFromEmbedById } from './utils'

const RENDERABLE_CAMPAIGN_TYPES = ['EMAIL_CAPTURE', 'ANNOUNCEMENT_ONE_TIME']
const PERSONALIZATION_TOKEN_REGEX = /\{\{[\s\w.'"\\|()]+\}\}/g

const generateCampaignFrame = (frameName: string, frameHeight: string, frameWidth: string, insertBefore?: string, verticalOffset?: number, horizontalOffset?: number) =>
  broadcast({
    topic: 'generate-new-frame',
    message: {
      name: frameName,
      height: frameHeight,
      width: frameWidth,
      path: `core/${frameName}`,
      alignment: getWidgetAlignment(),
      insertBefore:insertBefore,
      verticalOffset: verticalOffset,
      horizontalOffset: horizontalOffset
    }
  })

const ANNOUNCEMENT_CAMPAIGN_TYPE_HANDLERS = {
  SLIDER: (verticalOffset, horizontalOffset) => {
    generateCampaignFrame(WidgetFrames.SLIDER, '100%', '400px',WidgetFrames.CONTROLLER,verticalOffset, horizontalOffset)
    store.dispatch(toggleController({controllerOpen: true}))
  },
  TAKEOVER: async () => {
    try {
      generateCampaignFrame(WidgetFrames.TAKEOVER, '100%', '100%')
      const message = await markAnnouncementPlaybookOpened()
      markMessageAsRead(message?.conversationId, message?.id)
    } catch(e) {
      warn([e])
    }
  },
  WELCOME_MESSAGE: () => store.dispatch(toggleChat({ chatOpen: true }))
}

const dispatchRenderForEmailCaptureCampaign = () => generateCampaignFrame(WidgetFrames.EMAIL_CAPTURE, '0px', '416px')

const dispatchRenderForAnnouncementCampaign = (displayType: CampaignDisplayType, verticalOffset?:number, horizontalOffset?:number) => {
  const campaignRenderHandler = ANNOUNCEMENT_CAMPAIGN_TYPE_HANDLERS[displayType]
  if (campaignRenderHandler) {
    campaignRenderHandler(verticalOffset,horizontalOffset)
  }
}

/**
 * When a campaign is set to be displayed, we stash that campaignId away in sessionStorage so we can
 * provide it as a fallback if there's current playbook content to display to the visitor
 * *Note*: We only want to ever do this for bot campaigns - announcements will cause weird behavior and should not be shown as a fallback
 * @param campaignId - the campaign id that the widget is displaying to the visitor
 */
const saveSessionCampaignViewToStorage = (campaignId: number) => {
  const embedId = getEmbedId(getStoreState())
  localStore.merge(DRIFT_SESSION_CAMPAIGNS, { [embedId]: [campaignId] })
}

/**
 * Keeps track of what campaigns have been marked enrolled for a given load of the widget
 * This is used to:
 *  - Ensure that only eligible campaigns can be shown in a given tab
 *  - Keep a list of what campaignIds can be seen as fallbacks when there's no explicit content
 * @param campaignId - the campaign to mark as enrolled
 */
export const markEnrolledCampaign = (campaignId) => {
  store.dispatch(receiveEnrolledCampaign(campaignId))
  saveSessionCampaignViewToStorage(campaignId)
}

export const resolveAnnouncementMessage = async (matchingCampaign: EmbedCampaign): Promise<PrerenderedAnnouncementResult> => {
  let campaignMessage = getAttributeFromCampaignOrDefault(matchingCampaign, ['message'], '')
  let isAutomaticSender = getAttributeFromCampaignOrDefault(matchingCampaign, ['automaticSender'], false)
  let senderId = getAttributeFromCampaignOrDefault(matchingCampaign, ['senderId'], null)

  const ctaType = getAttributeFromCampaignOrDefault(matchingCampaign, ['cta', 'CtaType'], null)
  const isRandomSchedulingCTA = (!!ctaType && ctaType === CTAType.SCHEDULE_MEETING) && !senderId
  const messageContainsPersonalization = campaignMessage.match(PERSONALIZATION_TOKEN_REGEX) !== null

  const shouldPersonalizeMessage =
    messageContainsPersonalization ||
    isAutomaticSender ||
    isRandomSchedulingCTA

  if (shouldPersonalizeMessage) {
    const { session } = getStoreState()
    const endUserId: number = path(['endUser', 'id'], session)

    const { data } = await getPersonalizedAnnouncementMessage({
      campaignId: matchingCampaign.id,
      senderId: senderId,
      endUserId
    })

    campaignMessage = data.message
    senderId = data.senderId
  }

  const linkifiedMessage = linkifyMessageBody(campaignMessage)
  return { message: linkifiedMessage, senderId }
}

export const renderAnnouncementCampaign = async (campaignId: number, isDelayed: boolean) => {
  const state = getStoreState()
  const matchingCampaign = getCampaignFromEmbedById(campaignId)
  const chatOpen = isChatOpen(state)

  if (chatOpen || !matchingCampaign || !RENDERABLE_CAMPAIGN_TYPES.includes(matchingCampaign.type)) {
    return
  }

  store.dispatch(receiveEnrolledCampaign(campaignId))

  const displayType = getAttributeFromCampaignOrDefault(matchingCampaign, ['displayType'])
  const playbookId = getAttributeFromCampaignOrDefault(matchingCampaign, ['playbookId'])
  const { message: announcementMessage, senderId } = await resolveAnnouncementMessage(matchingCampaign)
  const announcementCampaign = Object.assign({}, {
    ...matchingCampaign,
    attributes: {
      ...matchingCampaign.attributes,
      senderId
    }
  })

  triggerPlaybookFiredAPIEvent({
    id: null,
    authorId: senderId,
    attributes: {
      playbookId,
      campaignId,
    },
    conversationId: null
  })

  assignActiveCampaignFromEmbed(announcementCampaign, announcementMessage)
  sendPlaybookImpression({ playbookId, isVirtual: true, isDelayed: isDelayed })

  const verticalOffset = getVerticalOffset(state)
  const horizontalOffset = getHorizontalOffset(state)

  matchingCampaign.type === CampaignDisplayType.EMAIL_CAPTURE ?
    dispatchRenderForEmailCaptureCampaign() :
    dispatchRenderForAnnouncementCampaign(displayType, verticalOffset, horizontalOffset)
}

export const renderBotCampaign = async ({
  campaignId,
  isDelayed = false,
  playbookId,
}: BotCampaignRecord): Promise<RenderBotCampaignResult> => {
  markEnrolledCampaign(campaignId)

  const campaignRefreshToken = getCampaignRefreshToken(store.getState())
  const locale = getEmbedLocale()
  const senderId = getBotSenderId()

  try {
    const matchingCampaign = getCampaignFromEmbedById(campaignId)
    const interactionId: number = path(['attributes', 'interactionId'], matchingCampaign)
    const inboxId: number = path(['inboxId'], matchingCampaign)

    const conversationPreview = await fetchPreRenderedPlaybookMessages({
      playbookId,
      campaignId,
      interactionId,
      inboxId
    })

    const messages = conversationPreview?.messages ?? []

    if (!conversationPreview || !messages.length) {
      enrollCampaign(campaignId, senderId, campaignRefreshToken, locale)
      return RenderBotCampaignResult.ENROLLED
    }

    assignActiveCampaignFromEmbed(matchingCampaign)

    sendPlaybookImpression({
      isDelayed,
      isExperiment: conversationPreview.isExperiment,
      isVirtual: true,
      playbookId,
      playbookVersion: conversationPreview.playbookVersion
    })

    triggerPlaybookFiredAPIEvent({
      ...messages?.[0] ?? {},
      id: null,
      conversationId: null
    })

    store.dispatch(receivePreRenderedMessages({
      campaignId,
      messages,
      resumeFlowContext: reject(isNil, conversationPreview.resumeFlowContext)
    }))

    return RenderBotCampaignResult.RENDERED
  } catch (err) {
    enrollCampaign(campaignId, senderId, campaignRefreshToken, locale)
    return RenderBotCampaignResult.ENROLLED
  }
}

/**
 * Fetches pre-rendered messages for a playbook if it doesn't require a conversation record to display
 * Flow-api will return a 200 if it was able to pre-render and a 204 in case it wasn't able to
 * However we want to split logic based on "we have messages" or "we don't have messages"
 * so this will gracefully fallback to enroll for 4xx and 5xx errors
 * @param playbookId (number) - id of playbook to pre-render
 * @param campaignId (number) - id of campaign to pre-render
 * @param interactionId (number) - interactionId of campaign to pre-render
 * @param inboxId (number) - designation of which inbox to assign the pre-rendered messages to
 * @param locale (string) - language in which the pre-rendered messages are displayed in
 */
export const fetchPreRenderedPlaybookMessages = async ({ playbookId, campaignId, interactionId, inboxId }): Promise<PrerenderedBotCampaign> => {
  try {
    const {
      locale,
      ...clientSideCtx
    } = getContextForPreRenderedCampaigns(getStoreState())

    const response = await getInitialBotMessagesForPlaybook({
      playbookId,
      campaignId,
      interactionId,
      inboxId: inboxId,
      locale,
      clientSideContext: clientSideCtx
    })


    if (response.status !== 200) {
      return null
    }

    return response.data
  } catch(err) {
    warn([`Failed to fetch pre-rendered playbook data for ${playbookId}`])
    return null
  }
}
